import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'

type AccuracyTaskOutcomeResponse = {
  tasks_count: {
    total: number
    trend: number
  }
  tasks_audited: {
    percentage: number
    trend: number
  }
  audit: {
    avg: number
    trend: number
  }
  benchmarks: {
    total: number
    trend: number
  }
  benchmarks_avg: {
    avg: number
    trend: number
  }
  target_accuracy?: number
  audit_list: { timeslice: string; average: number; count: number }[]
  benchmark_list: { [date: string]: { percentage: number } }
}

export const PROGRAM_TASK_OUTCOME_STATS_KEY = 'program-task-outcome-stats'

export function useGetTaskOutcome(
  programId?: string,
  startDate?: string,
  endDate?: string,
  granularity?: string
) {
  return useQuery({
    queryKey: [PROGRAM_TASK_OUTCOME_STATS_KEY, programId, startDate, endDate, granularity],
    queryFn: async () => {
      const { data } = await apiClient.get(`/int/stats/program/task-outcome`, {
        params: {
          start_time: startDate,
          end_time: endDate,
          program_id: programId,
          granularity,
        },
      })

      return data
    },
    select: (data) => {
      const getTrend = (current?: number, previous?: number) => {
        return previous && current && previous > 0
          ? (current ?? -previous) / previous
          : current && current > 0
            ? 1
            : 0
      }

      return {
        ...data,
        tasks_count: {
          total: data?.taskCount?.current ?? 0,
          trend: getTrend(data?.taskCount?.current, data?.taskCount?.previous),
        },
        tasks_audited: {
          percentage: (data?.taskAuditedCount?.current ?? 0) / (data?.taskCount?.current || 1),
          trend: getTrend(data?.taskAuditedCount?.current, data?.taskAuditedCount?.previous),
        },
        audit: {
          avg: data?.taskAuditAvgAccuracy?.current ?? 0,
          trend: getTrend(data?.taskAuditAvgAccuracy?.current, data?.taskAuditAvgAccuracy?.previous),
        },
        benchmarks: {
          total: 0,
          trend: 0,
        },
        benchmarks_avg: {
          avg: 0,
          trend: 0,
        },
        target_accuracy: (data?.targetAccuracy ?? 0) / 100,
        audit_list: data.breakdown,
        benchmark_list: {},
      } as AccuracyTaskOutcomeResponse
    },
    enabled: Boolean(programId) && Boolean(startDate) && Boolean(endDate) && Boolean(granularity),
    refetchOnWindowFocus: false,
  })
}
